import { useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useShallow } from 'zustand/react/shallow';

import { RecordingBar } from '@/components/molecules/voice-recording/RecordingBar';
import { ConfirmDialog } from '@/components/atoms/dialog';
import { InlineBanner } from '@/components/atoms/banner';
import { useTimer } from '@/hooks';
import {
  getRemainingRecordingDisplay,
  getRemainingRecordingSeconds,
  getRemainingRecordingSeverity,
} from '@/models/recordingDuration';
import { useTranslation } from '@/i18n';
import { useVoiceRecordingStore } from '@/stores';
import { useMeetingAnalyticsStore } from '@/stores/useMeetingAnalyticsStore';

export const GlobalRecordingBar = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const trackContactPickerShown = useMeetingAnalyticsStore(
    (state) => state.trackContactPickerShown,
  );
  const {
    recording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    lowStorageWarning,
    dismissLowStorageWarning,
  } = useVoiceRecordingStore(
    useShallow((state) => ({
      recording: state.recording,
      pauseRecording: state.pauseRecording,
      resumeRecording: state.resumeRecording,
      stopRecording: state.stopRecording,
      lowStorageWarning: state.lowStorageWarning,
      dismissLowStorageWarning: state.dismissLowStorageWarning,
    })),
  );

  const [showStopDialog, setShowStopDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { status, elapsedMs, segmentStartedAt } = recording;
  const isRecordingActive = status === 'recording' || status === 'recording_paused';
  const isRecordingPaused = status === 'recording_paused';

  const elapsedSeconds = useTimer({
    isActive: isRecordingActive,
    isPaused: isRecordingPaused,
    baseElapsedMs: elapsedMs,
    segmentStartTime: segmentStartedAt,
  });

  const remainingSeconds = getRemainingRecordingSeconds(elapsedSeconds);
  const remaining = getRemainingRecordingDisplay(remainingSeconds);

  const handleAction = () => {
    if (isRecordingPaused) {
      void resumeRecording();

      return;
    }
    void pauseRecording();
  };

  const handleStop = () => {
    if (isSubmitting) {
      return;
    }

    setShowStopDialog(true);
  };

  const handleConfirmStop = async () => {
    setShowStopDialog(false);
    setIsSubmitting(true);
    const recordingSessionId = recording.recordingSessionId ?? null;

    try {
      const meetingId = await stopRecording();

      if (meetingId) {
        trackContactPickerShown({ meetingId, source: 'recording_flow', recordingSessionId });
        router.push({
          pathname: '/meeting/[id]',
          params: { id: meetingId, openAttendeePicker: 'true' },
        });

        return;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismissStop = () => {
    setShowStopDialog(false);
  };

  const handleExpand = () => {
    router.push('/recording');
  };

  if (!isRecordingActive || pathname === '/recording') {
    return null;
  }

  return (
    <>
      {lowStorageWarning ? (
        <InlineBanner
          message={t('recording.lowStorageWarning.message')}
          variant="warning"
          onDismiss={dismissLowStorageWarning}
          testID="low-storage-warning-banner"
        />
      ) : null}
      <RecordingBar
        elapsedSeconds={elapsedSeconds}
        isPaused={isRecordingPaused}
        remainingLabel={t(`recording.remaining.${remaining.unit}`, { value: remaining.value })}
        remainingSeverity={getRemainingRecordingSeverity(remainingSeconds)}
        disabled={isSubmitting}
        labels={{
          expand: t('recording.actions.expand'),
          playPause: isRecordingPaused
            ? t('recording.actions.resumeRecording')
            : t('recording.actions.pauseRecording'),
          stop: t('recording.actions.stopPlayback', { defaultValue: 'Stop' }),
        }}
        statusLabel={
          isRecordingPaused ? t('recording.indicator.paused') : t('recording.indicator.recording')
        }
        onAction={handleAction}
        onStop={handleStop}
        onExpand={handleExpand}
      />
      <ConfirmDialog
        visible={showStopDialog}
        title={t('recording.stopConfirmation.title')}
        body={t('recording.stopConfirmation.body')}
        confirmLabel={t('recording.stopConfirmation.confirm')}
        dismissLabel={t('recording.stopConfirmation.dismiss')}
        onConfirm={handleConfirmStop}
        onDismiss={handleDismissStop}
      />
    </>
  );
};
